import { Title, Stack, TextInput, NumberInput, Switch, Group, Button, Card, SimpleGrid, FileInput, Image, Box, Text } from "@mantine/core";
import defaultLogo from "../assets/boxtechs-logo.png";
import { useForm } from "@mantine/form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { configApi } from "../api/config.js";
import { notifications } from "@mantine/notifications";
import type { AppConfig } from "@cas/shared";
import { useEffect } from "react";

const retentionRule = (v: unknown) =>
  typeof v === "number" && Number.isFinite(v) && v >= 1 ? null : "must be a number ≥ 1";

export function Settings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["config"], queryFn: configApi.get });
  const form = useForm<AppConfig>({
    initialValues: {
      pipelineMode: "V6_LIVE", miscFolderId: "", supportForwardAddress: "", v6ShadowEmitEnabled: false,
      retentionEmailSoftDeleteDays: 30, retentionEmailHardDeleteDays: 60, retentionLogRetentionDays: 90,
      logoDataUri: "",
    },
    validate: {
      retentionEmailSoftDeleteDays: retentionRule,
      retentionEmailHardDeleteDays: retentionRule,
      retentionLogRetentionDays: retentionRule,
    },
  });
  useEffect(() => { if (data) form.setValues(data); form.resetDirty(data); }, [data]);

  const save = useMutation({
    // Re-GET to defeat the pipelineMode clobber race: if a peer flipped mode via
    // ModeBanner / setMode after this page loaded, we'd otherwise PUT the stale
    // mode back. Server-side split (PUT /manage/config refusing pipelineMode) is
    // the real fix and is on the plan-close backlog.
    mutationFn: async (v: AppConfig) => {
      const current = await configApi.get();
      return configApi.update({ ...v, pipelineMode: current.pipelineMode });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["config"] });
      notifications.show({ title: "Saved", message: "Settings updated", color: "green" });
    },
    onError: (e) => notifications.show({ title: "Save failed", message: String(e), color: "red" }),
  });

  const onLogoFile = (file: File | null) => {
    if (!file) return;
    if (file.size > 20000) {
      notifications.show({ title: "Logo too large", message: "Use an image under ~20 KB — an SVG works best.", color: "red" });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => form.setFieldValue("logoDataUri", String(reader.result));
    reader.onerror = () => notifications.show({ title: "Couldn't read file", message: "Please try a different image.", color: "red" });
    reader.readAsDataURL(file);
  };

  if (isLoading) return null;

  const onSave = () => {
    const v = form.validate();
    if (v.hasErrors) return;
    const turningOffShadow = data?.v6ShadowEmitEnabled === true && form.values.v6ShadowEmitEnabled === false;
    if (turningOffShadow && !confirm(
      "Disable V6 shadow emit?\n\nThis halts V6 → V7 decision emission and stops the diff classifier from collecting data. Re-enable from this page to resume."
    )) return;
    save.mutate(form.values);
  };

  return (
    <Stack maw={720} mx="auto">
      <Title order={2}>Settings</Title>
      <Card withBorder>
        <Stack>
          <TextInput label="Misc folder ID (Outlook entry ID for unknown senders)"
                     {...form.getInputProps("miscFolderId")} />
          <TextInput label="Support forward address"
                     {...form.getInputProps("supportForwardAddress")} />
          <Switch label="V6 shadow emit enabled"
                  description="When false, V6 won't POST decisions to V7 — kill-switch."
                  checked={form.values.v6ShadowEmitEnabled}
                  onChange={(e) => form.setFieldValue("v6ShadowEmitEnabled", e.currentTarget.checked)} />

          <Title order={4} mt="md">Branding</Title>
          <Group align="flex-end" gap="md">
            <Box style={{ background: "#fff", border: "1px solid #e7e3df", borderRadius: 8, padding: 12, minWidth: 190, display: "flex", alignItems: "center" }}>
              <Image src={form.values.logoDataUri || defaultLogo} alt="Portal logo" fit="contain" h={40} />
            </Box>
            <Stack gap={6} style={{ flex: 1 }}>
              <FileInput label="Portal logo" placeholder="Choose image…"
                         description="PNG, SVG, JPEG or WebP · under ~20 KB. Shows in the nav header."
                         accept="image/png,image/jpeg,image/svg+xml,image/webp"
                         clearable onChange={onLogoFile} />
              {form.values.logoDataUri
                ? <Button variant="subtle" color="gray" size="xs" style={{ alignSelf: "flex-start" }}
                          onClick={() => form.setFieldValue("logoDataUri", "")}>Reset to default logo</Button>
                : <Text size="xs" c="dimmed">Using the default BoxTechs logo. Save after choosing a file.</Text>}
            </Stack>
          </Group>

          <Title order={4} mt="md">Retention</Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
            <NumberInput label="Soft delete email after days" min={1} clampBehavior="strict" allowDecimal={false} {...form.getInputProps("retentionEmailSoftDeleteDays")} />
            <NumberInput label="Hard delete after days" min={1} clampBehavior="strict" allowDecimal={false} {...form.getInputProps("retentionEmailHardDeleteDays")} />
            <NumberInput label="Log retention days" min={1} clampBehavior="strict" allowDecimal={false} {...form.getInputProps("retentionLogRetentionDays")} />
          </SimpleGrid>
          <Group justify="flex-end">
            <Button onClick={onSave} loading={save.isPending} disabled={!form.isDirty() || save.isPending}>Save</Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
